import { connectedComponents } from "./connectedComponents";
import { aspectRatio, cropGray, fillRatio } from "./geometry";
import { otsuThreshold } from "./otsu";
import { threshold } from "./threshold";
import type { Component, DominoHalf, GrayImage } from "./types";
import type { DetectedTile } from "./tileDetection";

export interface PipCountingOptions {
  /** Smallest accepted pip side as a fraction of the tile's short side. */
  minPipSideFraction?: number;
  /** Largest accepted pip side as a fraction of the tile's short side. */
  maxPipSideFraction?: number;
  /** Maximum blob aspect ratio; rejects the dividing line and edge cracks. */
  maxPipAspect?: number;
  /** Minimum fill ratio; pips are roughly circular so fill ~0.6-0.85. */
  minPipFill?: number;
  /**
   * Absolute floor (0..255) applied to the per-tile Otsu threshold. The white
   * tile scores ~20-40 on the pip-score map while every pip colour scores well
   * above ~100, so this floor keeps blank halves from generating spurious blobs
   * out of sensor noise without dropping any real pip colour.
   */
  minScore?: number;
}

const DEFAULTS: Required<PipCountingOptions> = {
  minPipSideFraction: 0.06,
  maxPipSideFraction: 0.5,
  maxPipAspect: 2.2,
  // Real pips are round (fill ~0.75-0.8); this rejects the small, ragged edge
  // and shadow artifacts thrown up by photographed tiles.
  minPipFill: 0.6,
  minScore: 60,
};

/**
 * Count the pips on each half of a single detected tile.
 *
 * @param pipScore A pip-score map (see {@link toPipScore}) where higher values
 *   mark pixels that are far from the white tile colour — i.e. likely pips. Pass
 *   the score map for the *whole* image; the tile region is cropped internally.
 *
 * The tile region is cropped and thresholded so that pip pixels become
 * foreground. Blobs are filtered to circular, pip-sized components (excluding
 * the elongated dividing line) and then split across the tile's centre line.
 *
 * @returns The two halves ordered left→right (horizontal) or top→bottom
 *          (vertical), each in original-image coordinates.
 */
export function countPips(
  pipScore: GrayImage,
  tile: DetectedTile,
  options: PipCountingOptions = {},
): [DominoHalf, DominoHalf] {
  const opts = { ...DEFAULTS, ...options };
  const crop = cropGray(pipScore, tile.bbox);
  const t = Math.max(otsuThreshold(crop), opts.minScore);
  const binary = threshold(crop, { threshold: t, invert: false });
  const { components } = connectedComponents(binary, { connectivity: 8 });

  const shortSide = Math.min(tile.bbox.width, tile.bbox.height);
  const minSide = opts.minPipSideFraction * shortSide;
  const maxSide = opts.maxPipSideFraction * shortSide;

  const pips = components.filter((c) =>
    isPipShaped(c, minSide, maxSide, opts),
  );

  const horizontal = tile.orientation === "horizontal";
  const mid = horizontal ? crop.width / 2 : crop.height / 2;

  const halfA: DominoHalf = { pips: 0, pipCentroids: [] };
  const halfB: DominoHalf = { pips: 0, pipCentroids: [] };

  for (const pip of pips) {
    const coord = horizontal ? pip.centroid.x : pip.centroid.y;
    const target = coord < mid ? halfA : halfB;
    target.pips++;
    target.pipCentroids.push({
      x: pip.centroid.x + tile.bbox.x,
      y: pip.centroid.y + tile.bbox.y,
    });
  }

  return [halfA, halfB];
}

function isPipShaped(
  c: Component,
  minSide: number,
  maxSide: number,
  opts: Required<PipCountingOptions>,
): boolean {
  const { width: w, height: h } = c.bbox;
  if (w < minSide || h < minSide) return false;
  if (w > maxSide || h > maxSide) return false;
  if (aspectRatio(c.bbox) > opts.maxPipAspect) return false;
  if (fillRatio(c.area, c.bbox) < opts.minPipFill) return false;
  return true;
}
